import { isValidCountryCode } from 'src/utils/country';
import {
  BuyerWithSalesDto,
  CreateRetailSaleGroupInput,
  ListingDetails,
  MarketplacePaymentLineType,
  PointOfSaleSaleStatus,
  Ticket,
  TicketType,
} from 'src/WebApiController';

import {
  MultiListingSelectionRow,
  RetailSaleFormValues,
  RetailSalePaymentLine,
} from './types';

const roundTo2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Taxable base for the auto-generated buyer tax line (POS-8561): the total net proceeds plus the
 * net of the manual charges/credits. The managed Tax line itself is excluded so the calculation is
 * idempotent when the recomputed line is written back into `marketplacePaymentLines`.
 */
export const getRetailTaxableAmount = (
  totalNetProceeds: number | null | undefined,
  marketplacePaymentLines: RetailSalePaymentLine[] | null | undefined
): number => {
  const manualTotal = (marketplacePaymentLines ?? [])
    .filter((l) => l.paymentLineType !== MarketplacePaymentLineType.Tax)
    .reduce((sum, l) => sum + (l.amount ?? 0), 0);
  return (totalNetProceeds ?? 0) + manualTotal;
};

/** Tax amount = rate (a decimal fraction, e.g. 0.0825) × taxable base, rounded to cents. */
export const computeRetailTaxAmount = (
  taxRate: number,
  taxableAmount: number
): number => roundTo2(taxRate * taxableAmount);

export const getRetailExternalSaleId = (listingId: number) => {
  return `Retail-${listingId}-${Date.now()}`;
};

export const getRetailMerchantReferenceId = (saleId: number) => {
  return `Retail-${saleId}-${Date.now()}`;
};

/** Inclusive seat-range check; numeric when both bounds and the seat are numeric, else lexicographic. */
const isSeatInRange = (
  seat: string | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined
): boolean => {
  if (!from && !to) return true;
  const s = (seat ?? '').trim();
  const asNum = (v: string) => (/^\d+$/.test(v) ? Number(v) : null);
  const sn = asNum(s);
  const fn = from ? asNum(from) : null;
  const tn = to ? asNum(to) : null;

  // Numeric comparison when everything present is numeric.
  if (sn != null && (!from || fn != null) && (!to || tn != null)) {
    if (fn != null && sn < fn) return false;
    if (tn != null && sn > tn) return false;
    return true;
  }

  // Fallback to lexicographic comparison.
  if (from && s < from) return false;
  if (to && s > to) return false;
  return true;
};

/**
 * Infers the ticket ids to sell from the form's section/row/seat selection: the listing's available
 * tickets in the selected row whose seat falls within [seatFrom, seatTo]. Falls back to the first
 * quantitySold available tickets when no seat range is given (e.g. GA). Available = not sold, held,
 * spoiled, or wasted.
 */
export const resolveSaleTicketIds = (
  listing: ListingDetails | undefined,
  values: RetailSaleFormValues
): number[] => {
  const available = (listing?.tickets ?? []).filter(
    (t: Ticket) =>
      t.saleId == null &&
      t.holdId == null &&
      t.spoiledSaleId == null &&
      !t.isWasted
  );

  let matched = available;
  if (values.row) {
    matched = matched.filter((t) => (t.row ?? '') === values.row);
  }
  if (values.seatFrom || values.seatTo) {
    matched = matched.filter((t) =>
      isSeatInRange(t.seat, values.seatFrom, values.seatTo)
    );
  }

  const ids = matched.map((t) => t.id);
  const quantity = values.quantitySold ?? ids.length;
  return ids.slice(0, quantity);
};

/**
 * Multi-listing mode (POS-8649): derive the flat TicketIds set from the
 * per-row `availableTicketIds.slice(0, qtyToSell)` unions. Rows with qty=0
 * contribute nothing; row order is preserved so downstream diagnostics can
 * correlate ticket IDs back to their source row.
 *
 * The result is deduplicated because the backend contract is `ISet<long>` —
 * `SaleClient.createRetailSaleGroup` rejects duplicate ticket IDs with an
 * "item with the same key has already been added" error. Overlaps can leak
 * in when upstream data has anomalies (e.g. the same listing ID resolved
 * through two code paths); dedup here keeps the flow resilient to that.
 */
export const deriveTicketIdsFromSelections = (
  selections: MultiListingSelectionRow[] | null | undefined
): number[] => {
  if (!selections || selections.length === 0) return [];
  const flat = selections.flatMap((sel) =>
    sel.qtyToSell > 0 ? sel.availableTicketIds.slice(0, sel.qtyToSell) : []
  );
  return Array.from(new Set(flat));
};

/**
 * Builds the CreateRetailSaleGroupInput for the retail-sale flow. Every retail sale is created as a
 * sales group (a single sale is a group of one); the tickets to sell are inferred from the form's
 * section/row/seat selection via {@link resolveSaleTicketIds}.
 *
 * Multi-listing mode (POS-8649): when `values.perListingSelections` is present, ticket IDs are
 * derived from it via {@link deriveTicketIdsFromSelections} instead of the single-listing
 * section/row/seatFrom/seatTo/quantitySold form fields (which are unset in that mode).
 */
export const buildRetailSaleGroupInput = (
  values: RetailSaleFormValues,
  listing: ListingDetails | undefined,
  selectedBuyer: BuyerWithSalesDto | undefined,
  includeExternalFields = true
): CreateRetailSaleGroupInput => {
  const isExternal = values.paymentMethodChoice === 'external';
  const externalFields =
    isExternal && includeExternalFields
      ? {
          externalPaymentMethodType: values.externalPaymentMethodType ?? null,
          externalPaymentNote: values.externalPaymentNote ?? null,
          externalPaymentAmount: values.externalPaymentAmount ?? null,
          externalPaymentDate: values.externalPaymentDate ?? null,
        }
      : {
          externalPaymentMethodType: null,
          externalPaymentNote: null,
          externalPaymentAmount: null,
          externalPaymentDate: null,
        };

  const perListingSelections = values.perListingSelections;
  const isMultiListing =
    perListingSelections != null && perListingSelections.length > 0;

  const ticketIds = isMultiListing
    ? deriveTicketIdsFromSelections(perListingSelections)
    : resolveSaleTicketIds(listing, values);

  return {
    ticketIds,
    salesGroupName: null,
    saleDate: values.saleDate ?? null,
    currencyCode: values.currencyCode ?? null,
    inHandDate: values.inHandDate ?? null,
    ticketType: (values.ticketType as TicketType) ?? null,
    buyerName: selectedBuyer?.buyerName ?? null,
    buyerEmail: selectedBuyer?.buyerEmail ?? null,
    buyerPhone: selectedBuyer?.buyerPhoneNumber ?? null,
    internalNotes: values.privateNotes ?? null,
    saleStatus: PointOfSaleSaleStatus.PendingFulfillment,
    totalNetProceeds: values.totalNetProceeds ?? null,
    lineItems:
      values.marketplacePaymentLines?.map((line) => ({
        paymentLineType: line.paymentLineType,
        amount: line.amount,
        ratePercent: line.ratePercent ?? null,
        reference: line.externalPaymentId || null,
        description: line.description || null,
        saleLineItemChargeReason: line.saleLineItemChargeReason ?? null,
        saleLineItemCreditReason: line.saleLineItemCreditReason ?? null,
      })) ?? null,
    ...externalFields,
  };
};

export const getPaymentAmount = (values: RetailSaleFormValues) => {
  const { totalNetProceeds, marketplacePaymentLines } = values;
  const marketplaceLinesCost = (marketplacePaymentLines ?? []).reduce(
    (ret, line) => {
      ret += line.amount ?? 0;
      return ret;
    },
    0
  );
  return (totalNetProceeds ?? 0) + marketplaceLinesCost;
};

export const getPaymentCountryCode = (
  selectedBuyer: BuyerWithSalesDto | undefined,
  knownCountries?: Record<string, string> | null
) => {
  const nameToCountryCode = Object.entries(knownCountries ?? {}).reduce(
    (acc, [code, name]) => {
      acc[name] = code;
      return acc;
    },
    {} as Record<string, string>
  );
  const contryName =
    selectedBuyer?.billingCountry ?? selectedBuyer?.shippingCountry;
  if (contryName) {
    // incase the country names are set as code
    if (isValidCountryCode(contryName)) {
      return contryName;
    }
    if (nameToCountryCode[contryName]) {
      const countryCode = nameToCountryCode[contryName];
      if (isValidCountryCode(countryCode)) {
        return countryCode;
      }
    }
  }

  return 'US';
};
